import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Globe, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { siteConfig } from "@/config/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  country: "",
  message: "",
  website: "", // honeypot
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Please enter your name";
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.message || form.message.trim().length < 5)
      e.message = "Tell us a little about your business";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data?.ok) {
        toast.success("Message received. A partner will reply within one working day.");
        setForm(initialForm);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Unable to send your message. Please email us directly.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-36 overflow-hidden"
    >
      <div
        className="vs-orb"
        style={{
          width: 620,
          height: 620,
          top: -140,
          right: -220,
          background: "var(--vs-ambient-purple)",
        }}
      />
      <div className="vs-container relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left — Contact info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-[#d4af37]" />
              <span className="text-overline">Contact</span>
            </div>
            <h2 className="mt-5 font-serif-display text-4xl md:text-5xl text-white tracking-tight leading-[1.05]">
              Speak with a
              <br />
              <span className="italic vs-gold-text">CFO partner.</span>
            </h2>
            <p className="mt-6 text-white/65 font-body leading-relaxed max-w-md">
              Reach us directly or send a note using the form. All enquiries are treated
              with strict confidentiality.
            </p>

            <div className="mt-10 space-y-6">
              <a
                data-testid="contact-email"
                href={`mailto:${siteConfig.contact.email}`}
                className="group flex items-start gap-4 border-t border-white/10 pt-5"
              >
                <span className="h-10 w-10 rounded-sm border border-white/15 flex items-center justify-center text-white/60 group-hover:border-[#d4af37] group-hover:text-[#d4af37] transition-colors">
                  <Mail size={16} />
                </span>
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-white/45 font-body">
                    Email
                  </div>
                  <div className="text-white font-body group-hover:text-[#d4af37] transition-colors">
                    {siteConfig.contact.email}
                  </div>
                </div>
              </a>

              <div className="group flex items-start gap-4 border-t border-white/10 pt-5">
                <span className="h-10 w-10 rounded-sm border border-white/15 flex items-center justify-center text-white/60">
                  <Globe size={16} />
                </span>
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-white/45 font-body">
                    Website
                  </div>
                  <div className="text-white font-body">{siteConfig.contact.website}</div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-5">
                <div className="text-[10px] tracking-widest uppercase text-white/45 font-body">
                  Presence
                </div>
                <div className="mt-1 font-serif-display text-xl text-white/90">
                  {siteConfig.contact.hqLabel}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right — Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            onSubmit={submit}
            noValidate
            data-testid="contact-form"
            className="lg:col-span-7 vs-glass rounded-sm p-8 md:p-12"
          >
            {/* Honeypot */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={(e) => set("website", e.target.value)}
              autoComplete="off"
              tabIndex="-1"
              aria-hidden="true"
              className="hidden"
              data-testid="contact-honeypot"
            />

            <div className="grid md:grid-cols-2 gap-5">
              <Field
                label="Full name"
                testid="input-name"
                value={form.name}
                onChange={(v) => set("name", v)}
                error={errors.name}
                required
              />
              <Field
                label="Company"
                testid="input-company"
                value={form.company}
                onChange={(v) => set("company", v)}
              />
              <Field
                label="Email"
                type="email"
                testid="input-email"
                value={form.email}
                onChange={(v) => set("email", v)}
                error={errors.email}
                required
              />
              <Field
                label="Phone"
                testid="input-phone"
                value={form.phone}
                onChange={(v) => set("phone", v)}
              />
              <Field
                label="Country"
                testid="input-country"
                value={form.country}
                onChange={(v) => set("country", v)}
                wrapClass="md:col-span-2"
              />
              <div className="md:col-span-2">
                <label className="text-[10px] tracking-[0.28em] uppercase text-white/50 font-body">
                  How can we help
                </label>
                <textarea
                  data-testid="input-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  className="mt-2 w-full bg-[#0f172a]/70 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-white/30 font-body focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37] outline-none transition-colors resize-none"
                  placeholder="Tell us briefly about your business, revenue range, and what you'd like a CFO view on."
                />
                {errors.message && (
                  <p className="mt-1 text-xs text-red-400" data-testid="error-message">
                    {errors.message}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
              <p className="text-[11px] text-white/40 font-body max-w-sm">
                By submitting, you agree to be contacted by a VerySight partner. We do not
                share your details with third parties.
              </p>
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="vs-btn-primary group"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send Message
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  error,
  wrapClass = "",
  testid,
}) {
  return (
    <div className={wrapClass}>
      <label className="text-[10px] tracking-[0.28em] uppercase text-white/50 font-body">
        {label} {required && <span className="text-[#d4af37]">*</span>}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-[#0f172a]/70 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-white/30 font-body focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37] outline-none transition-colors"
      />
      {error && (
        <p
          className="mt-1 text-xs text-red-400"
          data-testid={`error-${testid?.replace("input-", "")}`}
        >
          {error}
        </p>
      )}
    </div>
  );
}
